import React, { useEffect, useState } from 'react';

import { MoreOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  DatePicker,
  Dropdown,
  Flex,
  Form,
  GetProp,
  MenuProps,
  Table,
  TablePaginationConfig,
  TableProps,
  message,
} from 'antd';
import Column from 'antd/es/table/Column';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ApiErrorData } from 'models/ApiResponse';
import { ServerSideDataSchema } from 'models/ServerSideDataSchema';

import { TitaskIntegrationService } from 'services/TitaskIntegrationService';

import Layout from '../../../components/Layout/Layout';
import appI18n from '../../../config/i18n';
import { GeoVictoriaMigrationLogRowSchema } from '../../GeoVictoria/GeoVictoriaToBukMigration/GeoVictoriaToBukMigration';
import BukMigrationDetailModal from './Components/BukMigrationDetailModal';
import BukMigrationModal from './Components/BukMigrationModal';

import './BukToGeoVictoriaMigration.scss';

export interface BukMigrationLogRowSchema {
  id: number;
  key: string;
  type: string;
  status: string;
  createdAt: Date;
  finishedAt: Date | null;
  params: any;
  payload: any;
}

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Parameters<GetProp<TableProps, 'onChange'>>[1];
}

const PAGE_SIZE = 10;
const { RangePicker } = DatePicker;

function BukToGeoVictoriaMigration() {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const dateFormat: string = useSelector((state: any) => state.intl.dateFormat);
  const [dataSource, setDataSource] = useState<BukMigrationLogRowSchema[]>([]);
  const [startDateFilter, setStartDateFilter] = useState(
    dayjs().add(-30, 'day').format('YYYY-MM-DD')
  );
  const [endDateFilter, setEndDateFilter] = useState(
    dayjs().format('YYYY-MM-DD')
  );
  const [title] = useState<string>('');
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: PAGE_SIZE,
    },
  });
  const [bukMigrationLoading, setBukMigrationLoading] = useState(false);
  const [isBukMigrationModalOpen, setIsBukMigrationModalOpen] = useState(false);
  const [isBukMigrationDetailModalOpen, setIsBukMigrationDetailModalOpen] =
    useState(false);
  const [selectedLog, setSelectedLog] = useState<
    BukMigrationLogRowSchema | undefined
  >();

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    fetchData();
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    startDateFilter,
    endDateFilter,
  ]);

  const init = async () => {
    setLoading(true);

    await fetchData();

    setLoading(false);
  };

  const fetchData = async (currentPage?: number) => {
    setLoading(true);

    const getBukMigrationTriggerLogResponse =
      await TitaskIntegrationService.getBukMigrationTriggerLog({
        page: currentPage || tableParams.pagination?.current || 1,
        pageSize: tableParams.pagination?.pageSize || PAGE_SIZE,
        startDate: startDateFilter,
        endDate: endDateFilter,
      });

    if (getBukMigrationTriggerLogResponse.success) {
      const data =
        getBukMigrationTriggerLogResponse.data as ServerSideDataSchema<GeoVictoriaMigrationLogRowSchema>;
      setDataSource(data.data);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: parseInt(`${data.totalRowsFiltered}`),
        },
      });
    } else {
      const error = getBukMigrationTriggerLogResponse.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.buk.buk_to_geo_victoria_migration.scene.errors.fetch_grid'),
        3.5
      );
    }

    setLoading(false);
  };

  const getDropdownMenu = (
    row: BukMigrationLogRowSchema
  ): MenuProps['items'] => {
    return [
      {
        key: '1',
        label: (
          <span
            onClick={() => {
              showMigrationDetail(row);
            }}
          >
            <SearchOutlined />{' '}
            {t(
              'scenes.buk.buk_to_geo_victoria_migration.scene.dropdown.view_details'
            )}
          </span>
        ),
      },
    ];
  };

  const showMigrationDetail = (row: BukMigrationLogRowSchema) => {
    setSelectedLog(row);
    setIsBukMigrationDetailModalOpen(true);
  };

  const handleTableChange: TableProps<BukMigrationLogRowSchema>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setDataSource([]);
    }
  };

  const onFinish = (values: any) => {
    setStartDateFilter(values.range[0].format('YYYY-MM-DD'));
    setEndDateFilter(values.range[1].format('YYYY-MM-DD'));
  };

  const showBukMigrationModal = () => {
    if (!loading) {
      setIsBukMigrationModalOpen(true);
    }
  };

  const handleBukMigrationModalOk = async (date: string, type: string) => {
    if (!bukMigrationLoading) {
      setBukMigrationLoading(true);

      messageApi.open({
        key: 'submit-migration',
        type: 'loading',
        content: t(
          'scenes.buk.buk_to_geo_victoria_migration.scene.messages.execute_migration_loading'
        ),
        duration: 0,
      });

      const executeMigrationResponse =
        await TitaskIntegrationService.executeLicenseBukToGeoVictoriaManualMigration(
          {
            startDate: date,
            endDate: date,
            type,
          }
        );

      if (executeMigrationResponse.success) {
        setBukMigrationLoading(false);
        messageApi.destroy();
        messageApi.success(
          t(
            'scenes.buk.buk_to_geo_victoria_migration.scene.messages.execute_migration_success'
          ),
          3.5
        );
        setBukMigrationLoading(false);
        setIsBukMigrationModalOpen(false);
      } else {
        const error = executeMigrationResponse.data as ApiErrorData;
        messageApi.destroy();
        messageApi.error(
          (error.message as string) ||
            t(
              'scenes.buk.buk_to_geo_victoria_migration.scene.errors.execute_migration_failed'
            ),
          3.5
        );
        setLoading(false);
      }

      setBukMigrationLoading(false);
      await fetchData(1);
    }
  };

  const handleBukMigrationModalCancel = () => {
    if (!bukMigrationLoading) {
      setIsBukMigrationModalOpen(false);
    }
  };

  const handleBukMigrationDetailModalCancel = () => {
    setIsBukMigrationDetailModalOpen(false);
  };

  return (
    <>
      {contextHolder}
      <Layout title={title} breadcrumbMode="full">
        <Card>
          <Flex justify="space-between">
            <Form form={form} layout="inline" onFinish={onFinish}>
              <Form.Item
                name="range"
                label={t(
                  'scenes.buk.buk_to_geo_victoria_migration.scene.filters.date_range'
                )}
              >
                <RangePicker
                  allowClear={false}
                  defaultValue={[dayjs().add(-30, 'day'), dayjs()]}
                  format={dateFormat}
                />
              </Form.Item>
              <Form.Item shouldUpdate>
                {() => (
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {t(
                      'scenes.buk.buk_to_geo_victoria_migration.scene.filters.filter'
                    )}
                  </Button>
                )}
              </Form.Item>
            </Form>

            <Button
              style={{ float: 'right' }}
              type="primary"
              loading={loading}
              onClick={() => {
                showBukMigrationModal();
              }}
            >
              <PlusOutlined />{' '}
              {t(
                'scenes.buk.buk_to_geo_victoria_migration.scene.buttons.manual_migration'
              )}
            </Button>
          </Flex>
        </Card>

        <Card style={{ marginTop: '12px' }}>
          <Table<BukMigrationLogRowSchema>
            dataSource={dataSource}
            bordered
            loading={loading}
            size="small"
            scroll={{ x: 1300 }}
            pagination={tableParams.pagination}
            rowKey={(record) => record.id}
            onChange={handleTableChange}
          >
            <Column
              title={t(
                'scenes.buk.buk_to_geo_victoria_migration.scene.table.columns.id'
              )}
              dataIndex="id"
              key="id"
            />
            <Column
              title={t(
                'scenes.buk.buk_to_geo_victoria_migration.scene.table.columns.process_type'
              )}
              dataIndex="key"
              key="key"
              render={(key: string) => (
                <span>
                  {key === 'BUK_LICENCE_MIGRATION_TO_GEO_VICTORIA_PROCESS'
                    ? t(
                        'scenes.buk.buk_to_geo_victoria_migration.scene.table.key.licence_migration'
                      )
                    : key}
                </span>
              )}
            />
            <Column
              title={t(
                'scenes.buk.buk_to_geo_victoria_migration.scene.table.columns.mode'
              )}
              dataIndex="type"
              key="type"
              render={(type: string) => (
                <span>
                  {type === 'MANUAL'
                    ? t(
                        'scenes.buk.buk_to_geo_victoria_migration.scene.table.type.manual'
                      )
                    : t(
                        'scenes.buk.buk_to_geo_victoria_migration.scene.table.type.automatic'
                      )}
                </span>
              )}
            />
            <Column
              title={t(
                'scenes.buk.buk_to_geo_victoria_migration.scene.table.columns.status'
              )}
              dataIndex="status"
              key="status"
              render={(status: string) => (
                <span>
                  {status === 'OK'
                    ? t(
                        'scenes.buk.buk_to_geo_victoria_migration.scene.table.status.ok'
                      )
                    : status === 'PENDING'
                    ? t(
                        'scenes.buk.buk_to_geo_victoria_migration.scene.table.status.pending'
                      )
                    : t(
                        'scenes.buk.buk_to_geo_victoria_migration.scene.table.status.partial_finished'
                      )}
                </span>
              )}
            />
            <Column
              width={160}
              align="center"
              title={t(
                'scenes.buk.buk_to_geo_victoria_migration.scene.table.columns.created_at'
              )}
              dataIndex="createdAt"
              key="createdAtFormatted"
              render={(createdAt: Date) => (
                <span>{dayjs(createdAt).format(dateFormat)}</span>
              )}
            />
            <Column
              width={160}
              align="center"
              title={t(
                'scenes.buk.buk_to_geo_victoria_migration.scene.table.columns.finished_at'
              )}
              dataIndex="finishedAt"
              key="finishedAtFormatted"
              render={(finishedAt: Date) => (
                <span>
                  {finishedAt
                    ? dayjs(finishedAt).format(dateFormat)
                    : t(
                        'scenes.buk.buk_to_geo_victoria_migration.scene.table.status.not_finished'
                      )}
                </span>
              )}
            />
            <Column
              width={120}
              align="center"
              title=""
              key="actions"
              render={(row) => (
                <Dropdown
                  menu={{ items: getDropdownMenu(row) }}
                  placement="bottomLeft"
                  trigger={['click']}
                >
                  <Button size="small">
                    <MoreOutlined />
                  </Button>
                </Dropdown>
              )}
            />
          </Table>
        </Card>

        {/* Modals */}
        <BukMigrationModal
          isBukMigrationModalOpen={isBukMigrationModalOpen}
          handleBukMigrationModalOk={handleBukMigrationModalOk}
          handleBukMigrationModalCancel={handleBukMigrationModalCancel}
          loading={bukMigrationLoading}
        />

        {selectedLog && (
          <BukMigrationDetailModal
            selectedLog={selectedLog}
            isBukMigrationDetailModalOpen={isBukMigrationDetailModalOpen}
            handleBukMigrationDetailModalCancel={
              handleBukMigrationDetailModalCancel
            }
          />
        )}
      </Layout>
    </>
  );
}

export default BukToGeoVictoriaMigration;
